/**
 * LOGIBLOX i18n — lightweight runtime translation system
 * Detects browser language (de-DE/de-AT/de-CH → German, else English)
 * Uses data-i18n attributes to swap text content
 */

import { translations } from './translations';

const SUPPORTED_LANGS = ['en', 'de'];
const DEFAULT_LANG = 'en';
const STORAGE_KEY = 'logiblox_lang';

// DACH countries for geo-detection fallback
const GERMAN_LOCALES = ['de', 'de-de', 'de-at', 'de-ch'];

function detectLanguage() {
  // 1. Check URL parameter (for sharing links in specific language)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (urlLang && SUPPORTED_LANGS.includes(urlLang)) {
    setStoredLanguage(urlLang);
    return urlLang;
  }

  // 2. Check localStorage
  const stored = getStoredLanguage();
  if (stored) return stored;

  // 3. Check browser language
  const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (GERMAN_LOCALES.some(loc => browserLang === loc || browserLang.startsWith(loc + '-'))) {
    return 'de';
  }

  return DEFAULT_LANG;
}

function getStoredLanguage() {
  try {
    const lang = localStorage.getItem(STORAGE_KEY);
    return lang && SUPPORTED_LANGS.includes(lang) ? lang : null;
  } catch (e) {
    return null;
  }
}

function setStoredLanguage(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // ignore
  }
}

function getNestedValue(obj, key) {
  return key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
}

function applyTranslations(lang) {
  const t = translations[lang];
  if (!t) return;

  // Update html lang attribute
  document.documentElement.lang = lang;

  // Translate text content: data-i18n="key"
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    const val = getNestedValue(t, key);
    if (val !== undefined) {
      el.textContent = val;
    }
  });

  // Translate HTML content: data-i18n-html="key"
  document.querySelectorAll('[data-i18n-html]').forEach(el => {
    const key = el.getAttribute('data-i18n-html');
    const val = getNestedValue(t, key);
    if (val !== undefined) {
      el.innerHTML = val;
    }
  });

  // Translate placeholders: data-i18n-placeholder="key"
  document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
    const key = el.getAttribute('data-i18n-placeholder');
    const val = getNestedValue(t, key);
    if (val !== undefined) {
      el.placeholder = val;
    }
  });

  // Translate title attributes: data-i18n-title="key"
  document.querySelectorAll('[data-i18n-title]').forEach(el => {
    const key = el.getAttribute('data-i18n-title');
    const val = getNestedValue(t, key);
    if (val !== undefined) {
      el.title = val;
    }
  });

  // Translate alt attributes: data-i18n-alt="key"
  document.querySelectorAll('[data-i18n-alt]').forEach(el => {
    const key = el.getAttribute('data-i18n-alt');
    const val = getNestedValue(t, key);
    if (val !== undefined) {
      el.alt = val;
    }
  });

  // Update language switcher active state
  document.querySelectorAll('[data-lang-switch]').forEach(el => {
    const switchLang = el.getAttribute('data-lang-switch');
    el.classList.toggle('active', switchLang === lang);
  });
}

function switchLanguage(lang) {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  setStoredLanguage(lang);
  applyTranslations(lang);
}

function getCurrentLanguage() {
  return getStoredLanguage() || detectLanguage();
}

// Initialize on DOM ready
function init() {
  const lang = detectLanguage();
  setStoredLanguage(lang);
  applyTranslations(lang);

  // Bind language switcher clicks
  document.querySelectorAll('[data-lang-switch]').forEach(el => {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      const newLang = this.getAttribute('data-lang-switch');
      switchLanguage(newLang);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for external use
window.logibloxI18n = { switchLanguage, getCurrentLanguage };
